import { unref } from 'vue';

import { timeFormat } from 'd3-time-format';

import prepareCsvBreakdownData from '@/utils/prepareCsvBreakdownData';
import downloadCsvData from '@/utils/downloadCsvData';
import prepareSummaryExportData from '@/utils/prepareSummaryExportData';
import apiHelper from '@/utils/apiHelper';
import prepareExportData from '@/utils/prepareExportData';
import { track } from '@/utils/analytics';

import notifications from '@/messages/notifications';

import useFilterState from '@/state/useFilterState';
import useSectionState from '@/state/useSectionState';
import useNotificationsState from '@/state/useNotificationsState';

import {
  downloadNotification,
  successNotification,
  errorNotification,
} from '@/config/notifications';

const { addNotification, removeNotification } = useNotificationsState();

const getFormattedDate = (val) => timeFormat('%b %d, %Y')(val);

const {
  isSummarySection,
  sectionCharts,
  sectionTables,
  compsetNames,
  sectionName,
  getTableById,
} = useSectionState();

const {
  ctpTag,
  dateBegin,
  dateEnd,
  ctpDateBegin,
  ctpDateEnd,
  sdDateBegin,
  sdDateEnd,
  selectedMetric,
} = useFilterState();

const handleDownloadReport = async ({ format, id, recipient }) => {
  const isCtp = !!ctpTag.value;

  if (!format) {
    return;
  }

  let exportData;
  let isDownload = true;
  let successText = notifications.download.success;

  const infoId = addNotification({
    ...downloadNotification,
    text: notifications.download.pending.replace(/\[format\]/, format.toUpperCase()),
  });

  const dates = {
    startDate: getFormattedDate(dateBegin.value * 1000),
    endDate: getFormattedDate(dateEnd.value * 1000),
    ctpDateBegin: getFormattedDate(ctpDateBegin.value * 1000),
    ctpDateEnd: getFormattedDate(ctpDateEnd.value * 1000),
    sdDateBegin: getFormattedDate(sdDateBegin.value * 1000),
    sdDateEnd: getFormattedDate(sdDateEnd.value * 1000),
  };

  try {
    // PDF
    if (format === 'pdf') {
      if (!isSummarySection.value) {
        exportData = await prepareExportData({
          id,
          items: sectionCharts.value,
          dates,
        });
      } else {
        exportData = await prepareSummaryExportData({
          dates,
        });
      }
      // CSV
    } else if (format === 'csv') {
      if (id?.match(/breakdown/)) {
        const tableData = getTableById(id);

        isDownload = false;
        successText = notifications.email.successAsync;

        exportData = prepareCsvBreakdownData({
          isCtp,
          pipe: `${sectionName.value}/average`,
          selectedMetric: selectedMetric.value,
          dataFormat: unref(tableData.dataFormat),
          columnDescription: tableData.defaultColumnDescription,
          name: unref(tableData.defaultTitle),
          dates,
          recipients: [recipient],
        });

        track('csv - breakdown');
      } else {
        downloadCsvData({
          id,
          items: sectionTables.value,
          compsetNames: compsetNames.value,
          isCtp,
          dates,
        });

        track('csv - direct');
      }
    }

    // Make the request
    if (exportData) {
      await apiHelper(
        'POST',
        `/generate-${format}`,
        exportData,
        {
          type: isDownload ? 'download' : '',
          fileName: exportData.name,
        },
      );
    }

    addNotification({
      ...successNotification,
      text: successText,
    });
  } catch (err) {
    console.error(err);
    addNotification({
      ...errorNotification,
      text: notifications.download.error,
    });
  } finally {
    removeNotification(infoId);
  }
};

export default handleDownloadReport;
